import { Component, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { OrdersService } from "../../../../services/orders.service";
import { StoreService } from "../../../../services/store.service";
import { getFileNameFromHeaders } from "../../../../utils/general.utils";
import { DATE_TIME_FORMAT, getLocalDate } from "../../../../utils/date.utils";
import { userOptionLabel } from "../../../../utils/user.utils";
import { OrderModel } from "../../../../model/order.model";
import { map, tap } from "rxjs";

const ALLOWED_FILE_TYPE = 'application/pdf';

interface OrderCardHistoryRow {
  date: string;
  author: string;
  action: string;
}

@Component({
  selector: 'order-card-dialog',
  templateUrl: './order-card-dialog.component.html',
  styleUrls: ['./order-card-dialog.component.scss'],
})
export class OrderCardDialogComponent {
  private ordersService = inject(OrdersService);
  private store = inject(StoreService);
  protected data: OrderModel = inject(MAT_DIALOG_DATA);
  public dialogRef = inject(MatDialogRef<OrderCardDialogComponent>);

  protected fileName = '';
  private fileToUpload: File | null = null;

  protected get canSendCard(): boolean {
    return this.data.inspectionCardStatus === 'DOWNLOADED';
  }

  protected get canDeleteCard(): boolean {
    return this.data.inspectionCardStatus === 'SENT';
  }

  protected history: OrderCardHistoryRow[] = [];
  protected historyLoading = false;

  protected loadHistory(): void {
    this.store.showLoader();
    this.ordersService.getSummaryCardHistory(this.data.id).pipe(
      map(history => history
        .slice()
        .sort((a, b) => b.modifiedOn.localeCompare(a.modifiedOn))
        .map(item => ({
          date: getLocalDate(item.modifiedOn, DATE_TIME_FORMAT),
          author: userOptionLabel(item.modifiedBy),
          action: item.action
        }))
      )
    ).subscribe(history => {
      this.history = history;
      this.store.hideLoader();
    });
  }

  protected download() {
    this.store.showLoader();
    this.ordersService.downloadSummaryCard(this.data.id).subscribe(response => {
      this.store.saveFile(getFileNameFromHeaders(response), response.body as Blob);
      this.data.inspectionCardStatus = 'DOWNLOADED';
    });
  }

  protected openFilePicker(input: HTMLInputElement) {
    if (this.canSendCard) {
      input.click();
    }
  }

  protected selectFile(event: Event, input: HTMLInputElement) {
    const file = (event.target as HTMLInputElement).files?.[0];

    if (!file) {
      return;
    }

    if (file.type !== ALLOWED_FILE_TYPE) {
      this.store.showWarningMsg('FIELD_ERROR.TYPE_PDF');
      input.value = '';
      return;
    }

    this.fileToUpload = file;
    this.fileName = file.name;
  }

  protected send() {
    if (!this.fileToUpload || !this.canSendCard) {
      return;
    }

    const formData = new FormData();
    formData.append('file', this.fileToUpload, this.fileName);

    this.store.showLoader();
    this.ordersService.sendSummaryCard(this.data.id, formData).pipe(
      tap(() => {
        this.store.showSuccessMsg('ORDERS.CARD.SEND_SUCCESS');
        this.fileToUpload = null;
        this.fileName = '';
      })
    ).subscribe(() => this.store.hideLoader());
  }

  protected delete() {
    this.store.showLoader();
    this.ordersService.deleteSummaryCard(this.data.id).pipe(
      tap(() => {
        this.store.showSuccessMsg('ORDERS.CARD.DELETE_SUCCESS');
        this.data.inspectionCardStatus = 'DOWNLOADED';
        this.store.hideLoader();
      })
    ).subscribe();
  }
}
